import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useMemo} from 'react';
import {NavLink, useLocation, useNavigate} from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import {useAppDispatch} from '../../store/hooks';
import {createSessionVideoAsync} from '../../store/sessionVideoSlice';
import {getPlayerSessionDetailAsync} from '../../store/playerSessionSlice';
import {getTeamSessionDetailAsync} from '../../store/teamSessionSlice';
import {SessionVideo, SessionVideoSegment} from '../../interfaces';
import {DateTime} from 'luxon';
import {useTranslation} from 'react-i18next';
import {getDisplayStartTime} from '../../services/videoService';
import SessionVideoSegmentNotOverlappingSessionWarning from './VideoManagement/SessionVideoSegmentNotOverlappingSessionWarning';
import SessionVideoPlayer, {SessionVideoPlayerItem} from './SessionVideoPlayer';

interface Props {
  sessionVideos: SessionVideo[];
  sessionType: 'player' | 'team';
  teamId?: number;
  profileId?: number;
  sessionId: number;
  sessionStartTime?: string;
  sessionEndTime?: string;
  hideVideoPlayer?: boolean;
}

const SessionVideoManager: React.FC<Props> = ({
  sessionVideos,
  sessionType,
  teamId,
  profileId,
  sessionId,
  sessionStartTime,
  sessionEndTime,
  hideVideoPlayer,
}) => {
  const {t} = useTranslation();

  const dispatch = useAppDispatch();
  const {hash} = useLocation();
  const navigate = useNavigate();

  const allSessionVideos = useMemo(
    () =>
      (sessionVideos || [])
        .flatMap(sv => sv.sessionVideoSegments)
        .sort((a, b) => {
          if (
            (a?.userSelectedStartTime || a?.startTime) &&
            (b?.userSelectedStartTime || b?.startTime)
          ) {
            return (
              DateTime.fromISO(
                (a.userSelectedStartTime ?? a.startTime) as string,
              )
                .toJSDate()
                .getTime() -
              DateTime.fromISO(
                (b.userSelectedStartTime ?? b.startTime) as string,
              )
                .toJSDate()
                .getTime()
            );
          }

          // default return
          return 0;
        }),
    [sessionVideos],
  );
  const videoItems: SessionVideoPlayerItem[] = useMemo(
    () =>
      allSessionVideos
        .filter((sv): sv is SessionVideoSegment => !!sv?.videoUrl)
        .map(sv => ({
          id: sv.id as number,
          videoUrl: sv.videoUrl,
          displayLabel: getDisplayStartTime(sv) || sv.fileName,
        })),
    [allSessionVideos],
  );

  const createSessionVideo = useCallback(async () => {
    if (sessionType === 'player') {
      const createRes = await dispatch(
        createSessionVideoAsync({
          playerSessionId: sessionId,
        }),
      );
      const sessionVideoId = createRes?.payload?.sessionVideo?.id;
      if (sessionVideoId) {
        navigate(
          `/playersessions/${sessionId}/sessionvideos/${sessionVideoId}#add-video`,
        );
      }
      dispatch(getPlayerSessionDetailAsync(sessionId));
    } else if (sessionType === 'team') {
      const createRes = await dispatch(
        createSessionVideoAsync({
          teamSessionId: sessionId,
        }),
      );
      const sessionVideoId = createRes?.payload?.sessionVideo?.id;
      if (sessionVideoId) {
        navigate(
          `/teams/${teamId}/teamsessions/${sessionId}/sessionvideos/${sessionVideoId}#add-video`,
        );
      }
      await dispatch(getTeamSessionDetailAsync(sessionId));
    }
  }, [dispatch, navigate, sessionId, sessionType, teamId]);

  useEffect(() => {
    if (!sessionVideos?.length && hash === '#add-video') {
      createSessionVideo();
    }
  }, [createSessionVideo, hash, sessionVideos?.length]);

  return (
    <Box sx={{mb: 5}}>
      {(sessionVideos || []).length > 0 ? (
        <>
          <TableContainer sx={{mb: 1}} component={Paper}>
            <Table
              aria-label={t('ariaLabels.tables.sessionVideoManager') as string}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('general.processed')}</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(sessionVideos || []).map((row: SessionVideo) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {(row.playerSessionVideoClips ?? []).length > 0 ? (
                        <CheckIcon />
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        component={NavLink}
                        to={
                          sessionType === 'team'
                            ? `/teams/${teamId}/teamsessions/${sessionId}/sessionvideos/${row.id}`
                            : teamId
                              ? `/teams/${teamId}/players/${profileId}/playersessions/${sessionId}/sessionvideos/${row.id}`
                              : profileId
                                ? `/family/children/${profileId}/playersessions/${sessionId}/sessionvideos/${row.id}`
                                : `/playersessions/${sessionId}/sessionvideos/${row.id}`
                        }>
                        {t('video.addSessionVideos')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {sessionType === 'team' &&
              videoItems.length > 0 &&
              !hideVideoPlayer && (
                <Box sx={{p: 2}}>
                  <SessionVideoPlayer
                    videoItems={videoItems}
                    renderCard={({item, active, onClick}) => {
                      const sv = item as SessionVideoPlayerItem & {
                        displayLabel?: string;
                      };
                      return (
                        <Box
                          onClick={onClick}
                          sx={{
                            width: 210,
                            minWidth: 210,
                            mx: 1,
                            borderRadius: 1,
                            border: theme =>
                              `2px solid ${
                                active
                                  ? theme.palette.primary.main
                                  : theme.palette.divider
                              }`,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 1,
                            minHeight: 60,
                          }}>
                          <Typography
                            variant="body2"
                            sx={{fontWeight: 700, textAlign: 'center'}}>
                            {sv.displayLabel}
                          </Typography>
                        </Box>
                      );
                    }}
                  />
                </Box>
              )}
          </TableContainer>
          {/* show an alert if the session video doesn't overlap the session start and end time */}
          <SessionVideoSegmentNotOverlappingSessionWarning
            sessionVideoSegments={
              sessionVideos?.[0]?.sessionVideoSegments ?? []
            }
            sessionStartTime={sessionStartTime}
            sessionEndTime={sessionEndTime}
          />
        </>
      ) : null}
      {(sessionVideos || []).length === 0 && (
        <Button variant="contained" onClick={createSessionVideo}>
          {t('video.createSessionVideo')}
        </Button>
      )}
    </Box>
  );
};

export default SessionVideoManager;
